import {
  type ReactNode,
  type ReactElement,
  forwardRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Heading variants using class-variance-authority
 */
const headingVariants = cva('', {
  variants: {
    level: {
      1: 'text-lg font-semibold',
      2: 'text-lg font-semibold',
      3: 'font-semibold',
      4: 'font-medium text-sm',
    },
  },
  defaultVariants: {
    level: 1,
  },
});

export interface HeadingProps
  extends ComponentPropsWithoutRef<'h1'>, VariantProps<typeof headingVariants> {
  /**
   * The heading level (1-4)
   * @default 1
   */
  level?: 1 | 2 | 3 | 4;

  /**
   * The semantic HTML element to render (h1, h2, h3, h4)
   * Defaults to the heading level (h1 for level 1, etc.)
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4';

  /**
   * The content to display
   */
  children: ReactNode;
}

/**
 * Base Heading component with variant support
 */
const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 1, as, children, className, ...props }, ref): ReactElement => {
    const Component = as || (`h${level}` as 'h1' | 'h2' | 'h3' | 'h4');

    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level }), className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Heading.displayName = 'Heading';

/**
 * Heading1 - Page titles
 * @example
 * ```tsx
 * <Heading1>Page Title</Heading1>
 * ```
 */
export const Heading1 = forwardRef<
  HTMLHeadingElement,
  Omit<HeadingProps, 'level'>
>(({ as = 'h1', ...props }, ref) => (
  <Heading ref={ref} level={1} as={as} {...props} />
));
Heading1.displayName = 'Heading1';

/**
 * Heading2 - Section headings
 * @example
 * ```tsx
 * <Heading2 className="mb-2">Section Title</Heading2>
 * ```
 */
export const Heading2 = forwardRef<
  HTMLHeadingElement,
  Omit<HeadingProps, 'level'>
>(({ as = 'h2', ...props }, ref) => (
  <Heading ref={ref} level={2} as={as} {...props} />
));
Heading2.displayName = 'Heading2';

/**
 * Heading3 - Subsection headings
 * @example
 * ```tsx
 * <Heading3>Subsection Title</Heading3>
 * ```
 */
export const Heading3 = forwardRef<
  HTMLHeadingElement,
  Omit<HeadingProps, 'level'>
>(({ as = 'h3', ...props }, ref) => (
  <Heading ref={ref} level={3} as={as} {...props} />
));
Heading3.displayName = 'Heading3';

/**
 * Heading4 - Small headings
 * @example
 * ```tsx
 * <Heading4>Small Heading</Heading4>
 * ```
 */
export const Heading4 = forwardRef<
  HTMLHeadingElement,
  Omit<HeadingProps, 'level'>
>(({ as = 'h4', ...props }, ref) => (
  <Heading ref={ref} level={4} as={as} {...props} />
));
Heading4.displayName = 'Heading4';
